import api, { ValidateAxiosError } from '@/modules/api.service';
import { Contrib_AreaDto, Contrib_ServidorDto, Defensor_DefensorDto } from '@/api.types';
import { NAVIGATION_VIEW_SWITCH_USER_ID } from '@/shared/constants/navigation-view.enum';
import { UsuarioConfigs } from '@/shared/types/usuario_config';

export type DefensoriaSolarOpcao = {
  id: number;
  nome: string;
  comarcaId: number | null;
  nucleoId: number | null;
};

export type OpcaoSolar = {
  id: number;
  nome: string;
};
import { Endpoints } from '@/shared/constants/endpoints';
import { atendimentos_titular_input } from '@/api.types.extra';

export type LinhaAtendimentoTitular = {
  row_number?: number;
  data_atendimento: string;
  atendimento_numero: string;
  requerente_nome: string;
  telefone_numero?: string;
  qualificacao_nome?: string;
  historico_recepcao?: string;
  tipo_nome: string;
  tipo?: number;
  defensoria_nome: string;
  defensor_nome: string;
  substituto_nome?: string;
  atendido_por_nome: string;
  inicio?: string;
  termino?: string;
  presencial?: boolean;
  por_ligacao?: boolean;
  por_email?: boolean;
  por_whatsapp?: boolean;
  area_penal?: boolean;
};

export type LinhaHistoricoLogin = {
  cadastrado_em: string;
  logout: string;
  endereco_ip: string;
  info_navegador: string;
  usuario_id: number;
  username: string;
  nome: string;
};

export type UsuarioSolarLogin = {
  id: number;
  username: string;
  nome: string;
};

export type SolarRelatorioJob = {
  id: string;
  status: 'pendente' | 'processando' | 'concluido' | 'falha';
  erro?: string;
  dados?: LinhaAtendimentoTitular[];
};

export type SolarHistoricoLoginJob = {
  id: string;
  status: 'pendente' | 'processando' | 'concluido' | 'falha';
  erro?: string;
  dados?: LinhaHistoricoLogin[];
};

export type ParametrosAtendimentoTitular = {
  p_area_id: number;
  p_defensoria_id: number;
  p_defensor_id: number;
  p_servidor_id: number;
  p_tipo_atendimento: number;
  p_nucleo_id: number;
  p_data_inicial: string;
  p_data_final: string;
  filtros_texto?: string;
};

export class SolarService {
  async enfileirarAtendimentosTitular(
    parametros: ParametrosAtendimentoTitular,
  ): Promise<SolarRelatorioJob> {
    try {
      const response = await api.post<SolarRelatorioJob>(
        Endpoints.SOLAR + '/atendimentos/dados',
        { ...parametros, ...visaoSimulada() },
      );
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao enfileirar o relatório.');
    }
  }

  async consultarAtendimentosTitular(id: string): Promise<SolarRelatorioJob> {
    try {
      const response = await api.get<SolarRelatorioJob>(
        `${Endpoints.SOLAR}/atendimentos/dados/${id}`,
      );
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao consultar o relatório.');
    }
  }

  async relatorioAtendimentos(parametros: atendimentos_titular_input) {
    try {
      const p = {
        p_area_id: parametros.p_area_id ?? 0,
        p_defensoria_id: parametros.p_defensoria_id ?? 0,
        p_defensor_id: parametros.p_defensor_id ?? 0,
        p_servidor_id: parametros.p_defensoria_id ?? 0,
        p_tipo_atendimento: Number(parametros.p_tipo_atendimento ?? 0),
        p_data_inicial: parametros.p_data_inicial ?? '2000-01-01',
        p_data_final: parametros.p_data_final ?? '2000-01-01',
      };

      const response = await api.get(Endpoints.SOLAR + '/atendimentos', {
        params: p,
        responseType: 'blob',
      });

      return new Blob([response.data], { type: 'application/pdf' });
    } catch (error) {
      console.warn('Erro ao baixar o PDF:', error);
    }
  }

  async enumerateDefensorias(): Promise<DefensoriaSolarOpcao[]> {
    try {
      const response = await api.get<DefensoriaSolarOpcao[]>(Endpoints.SOLAR + '/tabelas/defensoria', { params: visaoSimulada() });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar defensorias.');
    }
  }

  async enumerateComarcas(): Promise<OpcaoSolar[]> {
    try {
      const response = await api.get<OpcaoSolar[]>(Endpoints.SOLAR + '/tabelas/comarca', { params: visaoSimulada() });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar comarcas.');
    }
  }

  async enumerateNucleos(): Promise<OpcaoSolar[]> {
    try {
      const response = await api.get<OpcaoSolar[]>(Endpoints.SOLAR + '/tabelas/nucleo', { params: visaoSimulada() });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar núcleos.');
    }
  }

  async enumerateDefensores(): Promise<Defensor_DefensorDto[]> {
    try {
      const response = await api.get(Endpoints.SOLAR + '/tabelas/defensor', { params: visaoSimulada() });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar defensores.');
    }
  }

  async enumerateServidores(): Promise<Contrib_ServidorDto[]> {
    try {
      const response = await api.get(Endpoints.SOLAR + '/tabelas/servidor', { params: visaoSimulada() });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar servidores.');
    }
  }

  async enumerateAreas(): Promise<Contrib_AreaDto[]> {
    try {
      const response = await api.get(Endpoints.SOLAR + '/tabelas/area');
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar areas.');
    }
  }

  async enumerateUsuariosLogin(): Promise<UsuarioSolarLogin[]> {
    try {
      const response = await api.get<UsuarioSolarLogin[]>(Endpoints.SOLAR + '/tabelas/usuario', { params: visaoSimulada() });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar usuários do Solar.');
    }
  }

  async enfileirarHistoricoLogin(parametros: {
    data_inicial: string;
    data_final: string;
    usuario_id: number;
    filtros_texto?: string;
  }): Promise<SolarHistoricoLoginJob> {
    try {
      const response = await api.post<SolarHistoricoLoginJob>(
        Endpoints.SOLAR + '/historico-login/dados',
        { ...parametros, ...visaoSimulada() },
      );
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao enfileirar o histórico de login.');
    }
  }

  async consultarHistoricoLogin(id: string): Promise<SolarHistoricoLoginJob> {
    try {
      const response = await api.get<SolarHistoricoLoginJob>(
        `${Endpoints.SOLAR}/historico-login/dados/${id}`,
        { params: visaoSimulada() },
      );
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao consultar o histórico de login.');
    }
  }

  async enumeratePredios(): Promise<Contrib_AreaDto[]> {
    try {
      const response = await api.get(Endpoints.SOLAR + '/tabelas/predio', { params: visaoSimulada() });
      return response.data;
    } catch (error) {
      return ValidateAxiosError(error, 'Falha ao listar prédios.');
    }
  }
}

function visaoSimulada() {
  const configs = UsuarioConfigs.GetConfigs();
  if (configs?.userId !== NAVIGATION_VIEW_SWITCH_USER_ID || configs.navigationViewOverride == null) return {};
  return { visao: configs.navigationViewOverride };
}